import FormControl from '@mui/material/FormControl/FormControl'
import InputLabel from '@mui/material/InputLabel/InputLabel'
import MenuItem from '@mui/material/MenuItem/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select/Select'
import Stack from '@mui/material/Stack/Stack'
import { useCallback, useMemo, useState } from 'react'

import { i18WithParams as t } from '@shared/locale'
import { LocationServiceTypeClientDto } from 'service-api/src/types/api'
import { defaultSearchParams, disabledST, waitListStatusKeys } from './helper'
import type { TSearchParams } from './types'

const allItems = 'all'

interface Props {
  serviceTypes: LocationServiceTypeClientDto[]
  onFilterParamsChange: (params: Partial<TSearchParams>) => void
}

export const Toolbar = ({ onFilterParamsChange, serviceTypes }: Props) => {
  const [initialParams] = useState(defaultSearchParams)
  const [service, setService] = useState(initialParams.service)
  const [waitlistStatus, setWaitlistStatus] = useState(initialParams.waitlistStatus)

  const activeServiceTypes = useMemo(() => {
    return serviceTypes.filter(
      (st) => (st.serviceType?.isVisit ?? false) && !disabledST.includes(st.serviceType?.name || '')
    )
  }, [serviceTypes])

  const buildFilterParams = useCallback(
    (overrides: Partial<TSearchParams> = {}): Partial<TSearchParams> => ({
      service,
      waitlistStatus,
      ...overrides,
    }),
    [service, waitlistStatus]
  )

  const emitFilterParamsChange = useCallback(
    (overrides: Partial<TSearchParams> = {}) => {
      onFilterParamsChange(buildFilterParams(overrides))
    },
    [buildFilterParams, onFilterParamsChange]
  )

  const onServiceHandler = useCallback(
    (event: SelectChangeEvent) => {
      const value = event.target.value
      setService(value)

      emitFilterParamsChange({ service: value })
    },
    [emitFilterParamsChange]
  )

  const onWaitlistStatusHandler = useCallback(
    (event: SelectChangeEvent) => {
      const value = event.target.value
      setWaitlistStatus(value)

      emitFilterParamsChange({ waitlistStatus: value })
    },
    [emitFilterParamsChange]
  )

  return (
    <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
      <FormControl sx={{ flexShrink: 0 }}>
        <InputLabel id="services-id">{t('waitlist.labels.services')}</InputLabel>
        <Select
          id="services-id"
          labelId="services-id"
          value={service}
          label={t('waitlist.labels.services')}
          size="small"
          sx={{ minWidth: '200px' }}
          onChange={onServiceHandler}
        >
          <MenuItem key={'all'} value={allItems}>
            {t('profile.waitlist.labels.allServices')}
          </MenuItem>
          {activeServiceTypes?.map((st) => (
            <MenuItem key={st.id} value={st.id || ''}>
              {st.displayName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl>
        <InputLabel id="status-id">{t('waitlist.labels.status')}</InputLabel>
        <Select
          id="status-id"
          labelId="status-id"
          value={waitlistStatus}
          label={t('waitlist.labels.status')}
          size="small"
          sx={{ minWidth: '200px' }}
          onChange={onWaitlistStatusHandler}
        >
          {waitListStatusKeys.map(([key, value]) => (
            <MenuItem key={key} value={key}>
              {value}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  )
}
